import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import {
  useReactTable,
  getPaginationRowModel,
  getCoreRowModel,
  getFilteredRowModel,
} from '@tanstack/react-table'
import { CustomerTableConsults } from './components/CustomerTableConsults'
import { useGetAllConsults } from '@/app/hooks/useGetAllConsults'
import { Consult } from '@/app/services/consultService/getAllConsults'
import { useConsultsMutations } from '@/app/hooks/useConsultsMutations'
import { useDebounce } from '@/app/hooks/useDebounce'

export const useTableConsultsController = () => {
  const { data, isFetching, isLoading, setParams } = useGetAllConsults({
    limit: 100,
  })

  const { deleteConsultMutation } = useConsultsMutations()

  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState(false)
  const [selectedConsultToDelete, setSelectedConsultToDelete] =
    useState<Consult | null>(null)
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const debouncedSearchValue = useDebounce(500, searchValue)

  const openDeleteConfirmationModal = (consult: Consult) => {
    setSelectedConsultToDelete(consult)
    setIsDeleteConfirmationModalOpen(true)
  }

  const closeDeleteConfirmationModal = () => {
    setSelectedConsultToDelete(null)
    setIsDeleteConfirmationModalOpen(false)
  }

  const confirmDeleteQuery = () => {
    if (!selectedConsultToDelete) return
    deleteConsultMutation.mutate(selectedConsultToDelete._id, {
      onSuccess: () => {
        closeDeleteConfirmationModal()
      },
    })
  }

  const { columns } = CustomerTableConsults(openDeleteConfirmationModal)

  const table = useReactTable({
    data: data?.data || [],
    columns,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      pagination: {
        pageIndex: 0,
        pageSize: data?.pagination.limit || 100,
      },
    },
  })

  // TODO: Implement export functionality
  // const fetchAllData = useCallback(
  //   () =>
  //     fetchGenericAllData(
  //       consultService.getAllConsults,
  //       data?.pagination.total || 0,
  //       params,
  //       setExportProgress,
  //     ),
  //   [data, params],
  // )

  // const handleExportToExcel = useCallback(async () => {
  //   setIsExporting(true)

  //   const titles = [
  //     'Cliente',
  //     'CPF',
  //     'Agendado',
  //     'Não Autorizado',
  //     'Valor',
  //     'CPF do Vendedor',
  //     'Vendedor',
  //     'Último saque',
  //   ]

  //   await exportToExcel({
  //     fetchData: fetchAllData,
  //     fileName: 'consultas.xlsx',
  //     titles,
  //     formatItem: (item: Consult) => ({
  //       cliente: item.name,
  //       CPF: masks.cpf(item.cpf),
  //       agendado: item?.available?.schedule
  //         ? masks.date(item.available.schedule)
  //         : '-',
  //       naoAutorizado:
  //         item.available.isAutorized === false ? 'Não autorizado' : '-',
  //       valor: masks.currency(item.available.amount ?? 0),
  //       CPFdoVendedor: item.seller?.cpf
  //         ? masks.cpf(item.seller.cpf)
  //         : 'Não disponível',
  //       vendedor: item.seller?.name || 'Não disponível',
  //       ultimoSaque: item.lastTransaction
  //         ? format(item.lastTransaction, 'dd/MM/yyyy, HH:mm')
  //         : 'Não possui saque',
  //     }),
  //   })

  //   setIsExporting(false)
  // }, [fetchAllData])

  const handleExportToExcel = useCallback(async () => {
    // TODO: Implement export functionality
  }, [])

  const setSearch = useCallback(() => {
    setParams((state) => ({ ...state, search: debouncedSearchValue }))
  }, [debouncedSearchValue, setParams])

  useEffect(() => {
    setSearch()
  }, [setSearch])

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
  }

  return {
    table,
    isFetching,
    isLoading,
    handleNextPage: () => table.nextPage(),
    handlePreviousPage: () => table.previousPage(),
    getPageIndex: () => table.getState().pagination.pageIndex,
    getPageCount: () => table.getPageCount(),
    isDeleteConfirmationModalOpen,
    openDeleteConfirmationModal,
    closeDeleteConfirmationModal,
    confirmDeleteQuery,
    isExporting: false,
    exportProgress: 0,
    handleExportToExcel,
    isImportModalOpen,
    setIsImportModalOpen,
    searchValue,
    handleSearchInputChange,
  }
}
